import moment from 'moment';

/**
 * Converts a Date object to UTC Time.
 *
 * @param date - Date to convert to UTC.
 * @returns Converted Date object.
 */
export function toUTCTime(date: Date): Date {
  const year: number = date.getFullYear();
  const month: number = date.getMonth();
  const day: number = date.getDate();
  const hour: number = date.getHours();
  const min: number = date.getMinutes();
  const sec: number = date.getSeconds();
  return new Date(Date.UTC(year, month, day, hour, min, sec));
}

/**
 * Determines whether or not a timestamp is in the past.
 *
 * @param expiryDatestamp - Date time string to validate.
 * @returns True if expired/in the past.
 */
export function isDateInPast(expiryDatestamp = ''): boolean {
  return moment(expiryDatestamp).isSameOrBefore(moment.utc());
}

/**
 * Determines whether or not a timestamp is in the past.
 *
 * @param expiryDatestamp - Date time to validate.
 * @returns True if expired/in the past.
 */
export function isDatePast(expiryDatestamp: Date): boolean {
  return moment(expiryDatestamp).isSameOrBefore(moment.utc());
}

/**
 * Given a date timestamp string, return a Date object or null.
 *
 * @param datestamp - Date string
 * @returns A date object if valid, otherwise null
 */
export function getDateOrNull(datestamp: string): Date | null {
  if (datestamp) {
    return moment.utc(datestamp).toDate();
  }
  return null;
}

/**
 * Convert a Date object into a localized string.
 *
 * @param d - Date object to localize.
 * @returns The formatted Date time string like this: September 4, 1986.
 */
export function localizedDisplayDate(d: Date): string {
  return moment(d).format('LL');
}

/**
 * Convert a Date object into a localized string, for yesterday.
 *
 * @param d - Date object to localize, yesterday.
 * @returns The formatted Date time string, for yesterday.
 */
export function localizedDisplayYesterday(d: Date): string {
  return localizedDisplayDate(moment(d).subtract(24, 'h').toDate());
}

/**
 * Validates that a given date is greater than Jan 1, 1900.
 *
 * @param d - Date to validate.
 * @returns True if the date is after Jan 1, 1900.
 */
export function isValidDate(d: Date): boolean {
  if (!d) {
    return false;
  }

  const earlyDate = new Date();
  earlyDate.setUTCFullYear(1900, 1, 1);
  earlyDate.setUTCHours(0, 0, 0, 0);

  return d > earlyDate;
}
